import { FieldType, isNullOrEmpty, ViewModelBase } from "@shoothill/core";
import { action, computed, observable, reaction, runInAction } from "mobx";
import type { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import moment from "moment";
import { AppUrls } from "AppUrls";
import { StoresInstance } from "Globals/Stores";
import validator from "validator";

// View models
import FormSectionViewModelBase from "Globals/ViewModels/FormSections/FormSectionViewModelBase";

// Models
import { BackgroundInformationModel } from "Globals/Models/Domain/FormSections/BackgroundInformation/BackgroundInformationModel";
import { FormSectionsEnum } from "Globals/Models/FormSectionsEnum";
import { BackgroundInformationDTO } from "Globals/Models/DTO/BackgroundInformationDTO";
import { isSpokenLanguageOfType } from "Globals/Helpers";
import { DisabilityImpactDTO, DisabilityImpactModel, SpokenLanguageType } from "Shared";

//note. I've done a quick reorganizing. the code itself is the same
//proper refactor requires a rewriting everything
export default class BackgroundInformationViewModel extends FormSectionViewModelBase<BackgroundInformationModel> {
    constructor(backgroundInformationModel: BackgroundInformationModel, _updateSectionCompleted: (index: FormSectionsEnum, value: boolean) => void) {
        super(_updateSectionCompleted, backgroundInformationModel);
        this.saveStore.addCallback(FormSectionsEnum.BackgroundInformation, this.save);
        this.saveFunction = this.save;
        if (this.updateSectionCompleted) {
            this.updateSectionCompleted(FormSectionsEnum.BackgroundInformation, this.canProceed);
        }
    }

    // ----- High-level public API -----

    public save = async (): Promise<void> => {
        try {
            let isMyModelValid: boolean = await this.isMyModelValid();
            let isMyModelDirty: boolean = this.isModelDirty;
            if (isMyModelValid && isMyModelDirty === true) {
                this.setIsLoading(true);
                this.saveStore.setIsSaving(true);

                let upsertBackgroundInformation: BackgroundInformationDTO = this.model.toDto();
                const apiResult = await this.Post<BackgroundInformationDTO>(AppUrls.Server.ApplicationForm.Upsert.BackgroundInformation, upsertBackgroundInformation);

                if (apiResult.wasSuccessful) {
                    this.model.fromDto(apiResult.payload);
                    console.info("BackgroundInformation:Upsert-Successful");
                    // Will set this section as completed when canProceed returns true
                    this.updateSectionCompleted(FormSectionsEnum.BackgroundInformation, this.canProceed);

                    this.setModelDirty(false);
                    return Promise.resolve();
                } else {
                    console.error("BackgroundInformation:Upsert-Error", apiResult.errors);
                    return Promise.reject(apiResult.errors);
                }
            }
        } catch (exception) {
            console.log("Error:", exception);
        } finally {
            this.setIsLoading(false);
            this.saveStore.setIsSaving(false);
            this.saveStore.setIsSaved(true);
        }
    };

    /**
     * Custom model validation function. Validates child view models.
     * @returns boolean If model is valid or not.
     */
    private isMyModelValid = async (): Promise<boolean> => {
        let isValid = true;
        if ((await this.isModelValid()) == false) {
            isValid = false;
        }
        return isValid;
    };

    @computed
    public get canProceed(): boolean {
        const fields = Object.keys(this.model) as Array<keyof FieldType<BackgroundInformationModel>>;
        for (const field of fields) {
            if (!this.shouldCheckFieldForCompletion(field)) continue;

            const value = this.model.getValue(field);
            if (this.isPlaceholderValue(value) || this.isEmptyNullOrUndefined(value) === true) {
                console.error("Field not completed: ", field, "Value: ", value);
                return false;
            }
        }
        return true;
    }

    /**
     * Maps model.disabilityImpacts back to the LookupStore instances by id so that
     * MUI Select reference-equality checks (===) match the option values and items appear ticked.
     */
    @computed
    public get disabilityImpactsAsStoreRefs(): DisabilityImpactModel[] {
        return this.model.disabilityImpacts
            .filter((di) => !di.isDeleted)
            .map((di) => StoresInstance.Domain.LookupStore.disabilityImpacts.find((s) => s.id === di.id))
            .filter((di): di is DisabilityImpactModel => di !== undefined);
    }

    public getValue(fieldName: keyof FieldType<BackgroundInformationModel>): any {
        if (fieldName === "disabilityImpacts") {
            return this.disabilityImpactsAsStoreRefs;
        }
        return super.getValue(fieldName);
    }

    @computed
    public get showSpokenLanguageOtherInput() {
        return isSpokenLanguageOfType(this.model.spokenLanguageId, SpokenLanguageType.Other);
    }

    @computed
    public get showSupportNeedsOtherInput(): boolean {
        const selected = StoresInstance.Domain.LookupStore.getSupportNeedsOptions.find((o) => o.value === this.model.supportNeedsId);
        return selected?.key?.toLowerCase() === "yes";
    }

    @computed
    public get showDevelopmentalConditionOtherInput(): boolean {
        const selected = StoresInstance.Domain.LookupStore.getDevelopmentalConditionOptions.find((o) => o.value === this.model.developmentalConditionId);
        return selected?.key?.toLowerCase() === "yes";
    }

    public async isFieldValid(fieldName: keyof FieldType<BackgroundInformationModel>, value: any): Promise<boolean> {
        return await this.isValid(fieldName, value);
    }

    // ----- Event handlers / UI interactions -----

    @action
    public handleInputChange = (): void => {
        this.clearConditionalModelData();
        this.saveStore.setIsSaved(false);
        this.restartTimer();
        this.saveFunction = this.save;
        this.updateSectionCompleted(FormSectionsEnum.BackgroundInformation, this.canProceed);
    };

    @action
    public handleCheckboxChange = (fieldName: keyof FieldType<BackgroundInformationModel>, val: boolean): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public handleFieldChange = (fieldName: keyof FieldType<BackgroundInformationModel>, val: string): void => {
        this.model.setValue(fieldName, val);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public handleRadioChange = (fieldName: keyof FieldType<BackgroundInformationModel>, val: string): void => {
        this.model.setValue(fieldName, val == "1" ? true : false);
        this.model.setDirty(fieldName, true);
        this.handleInputChange();
    };

    @action
    public setDate(fieldName: keyof FieldType<BackgroundInformationModel>, val: MaterialUiPickersDate) {
        if (val !== null) {
            this.model.setValue(fieldName, moment(val).utc().toISOString());
        } else {
            this.model.setValue(fieldName, val);
        }
        this.handleInputChange();
    }

    // ----- Model helpers -----

    /**
     * Clears conditional fields from the model.
     */
    private clearConditionalModelData(): void {
        this.clearEthnicityOtherIfNotRequired();
        this.clearSpokenLanguageOtherIfNoRequirements();
        this.clearPassportAndVisaFields();
        this.clearDisabilityImpactOtherDetailIfNotSelected();
        this.clearSupportNeedsOtherIfNotRequired();
        this.clearDevelopmentalConditionOtherIfNotRequired();
    }

    private clearDisabilityImpactOtherDetailIfNotSelected(): void {
        const hasOther = this.model.disabilityImpacts.filter((di) => !di.isDeleted).some((di) => (di.name ?? "").toLowerCase().includes("other"));
        if (!hasOther) {
            this.model.setValue("disabilityImpactOtherDetail", "");
        }
    }

    // ----- Low-level helpers for canProceed and validation -----

    private shouldCheckFieldForCompletion(field: keyof FieldType<BackgroundInformationModel>): boolean {
        if (this.isMetadataField(field)) return false;
        if (this.isDisabilityOtherField(field)) return false;
        if (field === "disabilityImpactOtherDetail" && !this.needsDisabilityImpactOtherDetail()) return false;
        if (field === "visaEndDate" && this.shouldSkipVisaEndDate()) return false;
        if (field === "ethnicityOther" && !this.needsEthnicityOther()) return false;
        if (field === "spokenLanguageOther" && !this.needsSpokenLanguageOther()) return false;
        if (field === "supportNeedsOther" && !this.showSupportNeedsOtherInput) return false;
        if (field === "developmentalConditionOther" && !this.showDevelopmentalConditionOtherInput) return false;
        return true;
    }

    private isMetadataField(field: keyof FieldType<BackgroundInformationModel>): boolean {
        return field === "createdDate" || field === "isDeleted" || field === "id" || field === "formId" || field === "applicantId";
    }

    private isDisabilityOtherField(field: keyof FieldType<BackgroundInformationModel>): boolean {
        if (field === "disabilityOther") {
            console.log("disability other.");
            return true;
        }
        return false;
    }

    private shouldSkipVisaEndDate(): boolean {
        return this.model.isPassportIndefinite === true || this.model.isBritishOrEUCitizen === true;
    }

    private needsEthnicityOther(): boolean {
        return StoresInstance.Domain.EthnicityStore.getRequiresOther(this.model.ethnicityId) == true;
    }

    private needsSpokenLanguageOther(): boolean {
        return this.model.spokenLanguageId === "other";
    }

    private needsDisabilityImpactOtherDetail(): boolean {
        return this.model.disabilityImpacts.filter((di) => !di.isDeleted).some((di) => (di.name ?? "").toLowerCase().includes("other"));
    }

    // ----- Low-level helpers moved from clearConditionalModelData -----

    private clearEthnicityOtherIfNotRequired(): void {
        if (StoresInstance.Domain.EthnicityStore.getRequiresOther(this.model.ethnicityId) === false) {
            this.model.setValue("ethnicityOther", "");
        }
    }

    private clearSpokenLanguageOtherIfNoRequirements(): void {
        if (isSpokenLanguageOfType(this.model.spokenLanguageId, SpokenLanguageType.NoRequirements)) {
            this.model.setValue("spokenLanguageOther", null);
        }
    }

    private clearSupportNeedsOtherIfNotRequired(): void {
        if (!this.showSupportNeedsOtherInput) {
            this.model.setValue("supportNeedsOther", null);
        }
    }

    private clearDevelopmentalConditionOtherIfNotRequired(): void {
        if (!this.showDevelopmentalConditionOtherInput) {
            this.model.setValue("developmentalConditionOther", null);
        }
    }

    private clearPassportAndVisaFields(): void {
        if (this.model.isBritishOrEUCitizen === true) {
            this.model.setValue("isPassportIndefinite", true);
            this.model.setValue("visaEndDate", null);
        }
        if (this.model.isPassportIndefinite === true) {
            this.model.setValue("visaEndDate", null);
        }
    }

    private isPlaceholderValue(value: any): boolean {
        return value === "placeholder";
    }

    public async isValid(fieldName: keyof FieldType<BackgroundInformationModel>, value: any, isRequired: boolean = false): Promise<boolean> {
        let { isValid, errorMessage } = await this.validateDecorators(fieldName);

        const isNonEmptyString = (v: any) => typeof v === "string" && !validator.isEmpty(v.trim());

        const presentOrRequired = (v: any) => {
            if (isRequired) return true;
            if (v === undefined || v === null) return false;
            if (typeof v === "string") return isNonEmptyString(v);
            // For non-strings (dates, numbers, booleans, objects) treat non-null/undefined as present
            return true;
        };

        const emptyOrPlaceholder = (v: any) => {
            if (v === undefined || v === null) return true;
            if (typeof v === "string") return validator.isEmpty(v.trim()) || v === "placeholder";
            return false;
        };

        // Special case: visa end date
        if (fieldName === "visaEndDate") {
            const date = value as Date | string | null;
            if (presentOrRequired(date)) {
                if (this.model.isPassportIndefinite === false && emptyOrPlaceholder(date)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }

        // Fields that follow the same required-or-placeholder rule
        const requiredFields = new Set(["ageId", "gender", "ethnicityId", "sexualOrientationId", "religionId", "disabilityId", "specialRequirementsId", "nationalityId"] as Array<
            keyof FieldType<BackgroundInformationModel>
        >);

        if (requiredFields.has(fieldName as any)) {
            if (presentOrRequired(value)) {
                if (emptyOrPlaceholder(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }

        // Nullable required fields (no placeholder pattern)
        const nullableRequiredFields = new Set(["spokenLanguageId", "developmentalConditionId", "supportNeedsId", "languageBarrierId"] as Array<
            keyof FieldType<BackgroundInformationModel>
        >);

        if (nullableRequiredFields.has(fieldName as any)) {
            if (isNullOrEmpty(value)) {
                errorMessage = "required to proceed";
                isValid = false;
            }
        }

        // Ethnicity "other" specialised check
        if (fieldName === "ethnicityOther") {
            if (presentOrRequired(value)) {
                if (StoresInstance.Domain.EthnicityStore.getRequiresOther(this.model.ethnicityId) === true && emptyOrPlaceholder(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }

        // Spoken language "other" specialised check
        if (fieldName === "spokenLanguageOther") {
            if (presentOrRequired(value)) {
                if (isNullOrEmpty(value) && isSpokenLanguageOfType(this.model.spokenLanguageId, SpokenLanguageType.Other)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }

        if (fieldName === "supportNeedsOther") {
            if (this.showSupportNeedsOtherInput && isNullOrEmpty(value)) {
                errorMessage = "required to proceed";
                isValid = false;
            }
        }

        if (fieldName === "developmentalConditionOther") {
            if (this.showDevelopmentalConditionOtherInput && isNullOrEmpty(value)) {
                errorMessage = "required to proceed";
                isValid = false;
            }
        }

        // Disability impact "other" specialised check
        if (fieldName === "disabilityImpactOtherDetail") {
            if (presentOrRequired(value)) {
                if (this.needsDisabilityImpactOtherDetail() && emptyOrPlaceholder(value)) {
                    errorMessage = "required to proceed";
                    isValid = false;
                }
            }
        }

        if (isValid === false) {
            console.log("Field Not valid: ", fieldName);
        }

        this.setError(fieldName, errorMessage);
        this.setValid(fieldName, isValid);
        return isValid;
    }

    /**
     * @returns boolean does this section have any errors
     */
    @action
    public async showUnfinishedFields(): Promise<boolean> {
        let hasError = false;
        for (const _field of Object.keys(this.model)) {
            let field = _field as keyof FieldType<BackgroundInformationModel>;
            let value = this.model.getValue(field);
            let isRequired = true;
            let isValid = await this.isValid(field, value, isRequired);
            if (isValid == false) {
                hasError = true;
            }
        }
        return hasError;
    }

    /**
     * When disabilityImpacts changes, merge new selection with deselected items that have existing DB records.
     * Deselected items with backgroundInformationDisabilityImpactId are kept with isDeleted=true so the server can delete them.
     */
    public beforeUpdate = (fieldName: keyof FieldType<BackgroundInformationModel>, value: any): any => {
        if (fieldName !== "disabilityImpacts") return undefined;

        const newSelection = (Array.isArray(value) ? value : []) as DisabilityImpactModel[];
        const oldImpacts = this.model.disabilityImpacts.slice();
        const newSelectionIds = new Set(newSelection.map((s) => s.id));

        // Items that were saved (have junction id) but are now deselected - keep with isDeleted=true
        const deselectedToKeep = oldImpacts.filter((old) => old.backgroundInformationDisabilityImpactId && !newSelectionIds.has(old.id));
        deselectedToKeep.forEach((d) => (d.isDeleted = true));

        // Build selected items - preserve backgroundInformationDisabilityImpactId from old when present
        const selectedModels = newSelection.map((storeItem) => {
            const existing = oldImpacts.find((o) => o.id === storeItem.id);
            const model = new DisabilityImpactModel();
            model.fromDto({
                id: storeItem.id,
                backgroundInformationDisabilityImpactId: existing?.backgroundInformationDisabilityImpactId ?? undefined,
                name: storeItem.name,
                type: storeItem.type,
                ordinal: storeItem.ordinal,
                isDeleted: false,
                otherDetail: existing?.otherDetail ?? storeItem.otherDetail,
            } as DisabilityImpactDTO);
            return model;
        });

        return [...selectedModels, ...deselectedToKeep];
    };
}
